import { useState, type FormEvent } from "react";

const FORM_ID = "db53vaoop8h4ruqk27dg";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db53vaoop8h4ruqk27dg";

type Status = "idle" | "loading" | "success" | "error";

export default function Subscribe() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "contact_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时没能提交，请稍后再试。");
    }
  }

  return (
    <section id="subscribe" className="w-full bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 lg:gap-14 items-center bg-primary-50 border border-primary-200 rounded-lg p-6 md:p-10">
          <div>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-primary-700 font-label">
              <i className="ri-mail-open-line"></i>
              Newsletter
            </span>
            <h2 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              订阅长日光阴，让旧时光按时抵达
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
              每两周一封，收录最新写下的情感故事、岁月往事与生活碎碎念，并附上一句适合当天的小提示。
              留下邮箱，我们慢慢把那些被时间冲淡的温暖，一封封寄回来。
            </p>
            <ul className="mt-6 flex flex-col gap-2.5 text-sm text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-secondary-600"></i>
                新故事优先送达，篇幅都不长
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-secondary-600"></i>
                附赠「回忆录写作」小提示
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-check-line text-secondary-600"></i>
                随时可以退订，不打扰你的生活
              </li>
            </ul>
          </div>

          <form
            id={FORM_ID}
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6 flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="sub-name" className="text-sm text-foreground-700">
                你的称呼
              </label>
              <input
                id="sub-name"
                name="name"
                type="text"
                placeholder="例如：林知夏"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="sub-email" className="text-sm text-foreground-700">
                邮箱 <span className="text-accent-600">*</span>
              </label>
              <input
                id="sub-email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="sub-topic" className="text-sm text-foreground-700">
                你最想读哪一类
              </label>
              <select
                id="sub-topic"
                name="topic"
                defaultValue="全部故事"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 cursor-pointer"
              >
                <option value="全部故事">全部故事</option>
                <option value="情感故事">情感故事</option>
                <option value="岁月往事">岁月往事</option>
                <option value="生活碎碎念">生活碎碎念</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="sub-message" className="text-sm text-foreground-700">
                想对长日光阴说的一句话（选填）
              </label>
              <textarea
                id="sub-message"
                name="message"
                rows={3}
                maxLength={500}
                placeholder="也可以只写下一段你正在想念的往事……"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 resize-none"
              ></textarea>
            </div>

            <input
              className="contact-verify-field"
              type="text"
              name="contact_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
              {status === "loading" ? "正在寄出…" : "订阅回忆录"}
            </button>

            {status === "success" && (
              <p className="flex items-center gap-2 text-sm text-secondary-800">
                <i className="ri-checkbox-circle-line text-base"></i>
                订阅成功，第一封回忆录很快就到。谢谢你愿意一起记住这些旧时光。
              </p>
            )}
            {status === "error" && (
              <p className="flex items-start gap-2 text-sm text-accent-700">
                <i className="ri-error-warning-line text-base mt-0.5"></i>
                {formError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}